'use client';

import { c as _c } from "react/compiler-runtime";
import { datadogRum } from '@datadog/browser-rum';
import type { Offer } from '@repo/contracts/marketplace/offers/types';
import { useSuspenseQuery } from '@tanstack/react-query';
import type { FlattenedFunnelConfig } from '@/features/funnel/config/flattened/types';
import { getBranchFromAnswers } from '@/features/funnel/config/utils/client';
import { useIsAnswerHydrated, useShouldSkipRecommendations, useSubmittedAnswers } from '@/features/funnel/state/answer/hooks';
import { fetchRecommendations } from './fetchRecommendations';
import type { RecommendationResponse, ResultsRecommendationResponse } from './types';
type UseRecommendationsOptions = {
  isResultsPage?: boolean;
};
const isResultsPageResponse = (data: unknown): data is ResultsRecommendationResponse => {
  return typeof data === 'object' && data !== null && 'categories' in data;
};
const isRecommendationResponse = (data: unknown): data is RecommendationResponse => {
  return typeof data === 'object' && data !== null && 'offers' in data;
};
export function useRecommendations(funnelFlattenedConfig: FlattenedFunnelConfig, options: {
  isResultsPage: true;
}): ResultsRecommendationResponse;
export function useRecommendations(funnelFlattenedConfig: FlattenedFunnelConfig, options?: {
  isResultsPage?: false;
}): readonly Offer[];
export function useRecommendations(funnelFlattenedConfig, t0) {
  const $ = _c(24);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    isResultsPage: t2
  } = t1;
  const isResultsPage = t2 === undefined ? false : t2;
  const submittedAnswers = useSubmittedAnswers();
  const isHydrated = useIsAnswerHydrated();
  const shouldSkipRecommendations = useShouldSkipRecommendations();
  let t3;
  if ($[2] !== funnelFlattenedConfig || $[3] !== submittedAnswers) {
    t3 = submittedAnswers ? getBranchFromAnswers(funnelFlattenedConfig, submittedAnswers) : null;
    $[2] = funnelFlattenedConfig;
    $[3] = submittedAnswers;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const branchId = t3;
  const shouldFetch = isHydrated && (isResultsPage || !shouldSkipRecommendations);
  let t4;
  if ($[5] !== isResultsPage) {
    t4 = isResultsPage ? {
      categories: []
    } : {
      offers: []
    };
    $[5] = isResultsPage;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const defaultData = t4;
  let t5;
  if ($[7] !== branchId || $[8] !== isResultsPage || $[9] !== shouldFetch || $[10] !== submittedAnswers) {
    t5 = isResultsPage ? ["results", "recommendations", branchId, submittedAnswers, shouldFetch] : ["recommendations", branchId, submittedAnswers, shouldFetch];
    $[7] = branchId;
    $[8] = isResultsPage;
    $[9] = shouldFetch;
    $[10] = submittedAnswers;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== branchId || $[13] !== defaultData || $[14] !== isResultsPage || $[15] !== shouldFetch || $[16] !== submittedAnswers) {
    t6 = async () => {
      if (!shouldFetch) {
        return defaultData;
      }
      if (!submittedAnswers || Object.keys(submittedAnswers).length === 0) {
        if (!isResultsPage) {
          datadogRum.addError(new Error("No submitted answers on funnel, skipping recommendations request"));
        }
        return defaultData;
      }
      if (!branchId) {
        datadogRum.addError(new Error("Could not derive a branch from submitted answers"));
        return defaultData;
      }
      const result = await fetchRecommendations(branchId, submittedAnswers, isResultsPage);
      return result ?? defaultData;
    };
    $[12] = branchId;
    $[13] = defaultData;
    $[14] = isResultsPage;
    $[15] = shouldFetch;
    $[16] = submittedAnswers;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t5 || $[19] !== t6) {
    t7 = {
      queryKey: t5,
      queryFn: t6
    };
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const {
    data
  } = useSuspenseQuery(t7);
  if (isResultsPageResponse(data)) {
    return data;
  }
  if (isRecommendationResponse(data)) {
    let t8;
    if ($[21] !== data.offers) {
      t8 = data.offers.map(_temp);
      $[21] = data.offers;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    return t8;
  }
  let t8;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = [];
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8 as readonly Offer[];
}
function _temp(t0) {
  const {
    offer
  } = t0;
  return offer;
}